import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import {
  CreateAdjustmentData,
  FranchiseFeePayout,
  FranchiseFeePayoutFilters,
  FranchiseFeePayoutListResponse,
  UpdateAdjustmentData,
} from './_types';
import {
  denormalizeFranchiseFeePayout,
  normalizeFranchiseFeePayout,
} from './normalizers';

export * from './_types';

export const FranchiseFeePayoutsQueryKey = 'franchise-fee-payouts';

export interface FranchiseFeePayoutDataParams {
  platformId?: string;
  payoutPeriodStart?: number;
  payoutPeriodEnd?: number;
  payoutStatus?: 'not_started' | 'in_progress' | 'completed' | 'failed';
  stripeTransferId?: string;
  notes?: string;
  meta?: Record<string, any>;
}

export interface FranchiseFeePayoutsParams {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  filters?: FranchiseFeePayoutFilters;
}

export const fetchFranchiseFeePayoutsQueryKey = (
  params: FranchiseFeePayoutsParams
) => [
  FranchiseFeePayoutsQueryKey,
  params.page,
  params.perPage,
  params.search,
  params.sortBy,
  params.sortDir,
  params.filters?.payoutStatus,
  params.filters?.payoutPeriodRange?.start,
  params.filters?.payoutPeriodRange?.end,
  params.filters?.sites,
  params.filters?.payerId,
  params.filters?.payerType,
];

export const fetchFranchiseFeePayouts = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  filters = {},
}: FranchiseFeePayoutsParams): Promise<FranchiseFeePayoutListResponse> => {
  const params = {
    page: page,
    per: perPage,
    ...(search && { 'q[notes_cont]': search }),
    ...(sortBy && {
      'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
    }),
    ...(filters.payoutStatus && {
      'q[payout_status_eq]': filters.payoutStatus,
    }),
    ...(filters.payoutPeriodRange && {
      'q[payout_period_start_gteq]': filters.payoutPeriodRange.start,
      'q[payout_period_end_lteq]': filters.payoutPeriodRange.end,
    }),
    ...(filters.sites && {
      sites: Array.isArray(filters.sites)
        ? filters.sites.join(',')
        : filters.sites,
    }),
    ...(filters.payerId && {
      'q[payer_id_eq]': filters.payerId,
    }),
    ...(filters.payerType && {
      'q[payer_type_eq]': filters.payerType,
    }),
  };

  const { data } = await api.get('/api/v4/hq/franchise_fee_payouts', {
    params,
  });

  // Handle both paginated and non-paginated responses
  if (data.franchise_fee_payouts && data.meta?.total !== undefined) {
    // Paginated response
    return {
      franchiseFeePayouts: data.franchise_fee_payouts.map(
        normalizeFranchiseFeePayout
      ),
      total: data.meta.total,
    };
  } else if (Array.isArray(data)) {
    // Non-paginated response (fallback)
    return {
      franchiseFeePayouts: data.map(normalizeFranchiseFeePayout),
      total: data.length,
    };
  } else {
    // Handle other response formats
    return {
      franchiseFeePayouts: [],
      total: 0,
    };
  }
};

export const fetchFranchiseFeePayout = async (
  id: string
): Promise<FranchiseFeePayout> => {
  const { data } = await api.get(`/api/v4/hq/franchise_fee_payouts/${id}`);
  return normalizeFranchiseFeePayout(data);
};

export const createFranchiseFeePayout = async (
  data: FranchiseFeePayoutDataParams
): Promise<FranchiseFeePayout> => {
  const { data: response } = await api.post(
    `/api/v4/hq/franchise_fee_payouts`,
    {
      hq_franchise_fee_payout: denormalizeFranchiseFeePayout(data),
    }
  );
  return normalizeFranchiseFeePayout(response);
};

export const updateFranchiseFeePayout = async (
  id: string,
  data: FranchiseFeePayoutDataParams
): Promise<FranchiseFeePayout> => {
  const { data: response } = await api.put(
    `/api/v4/hq/franchise_fee_payouts/${id}`,
    {
      hq_franchise_fee_payout: denormalizeFranchiseFeePayout(data),
    }
  );
  return normalizeFranchiseFeePayout(response);
};

export const deleteFranchiseFeePayout = async (id: string): Promise<void> => {
  await api.delete(`/api/v4/hq/franchise_fee_payouts/${id}`);
};

// Adjustment Management Methods

export const createFranchiseFeePayoutAdjustment = async (
  payoutId: string,
  data: CreateAdjustmentData
): Promise<FranchiseFeePayout> => {
  const { data: response } = await api.post(
    `/api/v4/hq/franchise_fee_payouts/${payoutId}/create_adjustment`,
    {
      adjustment: {
        amount: data.amount,
        reason: data.reason,
      },
    }
  );
  return normalizeFranchiseFeePayout(response);
};

export const updateFranchiseFeePayoutAdjustment = async (
  payoutId: string,
  adjustmentId: string,
  data: UpdateAdjustmentData
): Promise<FranchiseFeePayout> => {
  const { data: response } = await api.put(
    `/api/v4/hq/franchise_fee_payouts/${payoutId}/adjustments/${adjustmentId}`,
    {
      adjustment: {
        ...(data.amount !== undefined && { amount: data.amount }),
        ...(data.reason !== undefined && { reason: data.reason }),
      },
    }
  );
  return normalizeFranchiseFeePayout(response);
};

export const deleteFranchiseFeePayoutAdjustment = async (
  payoutId: string,
  adjustmentId: string
): Promise<FranchiseFeePayout> => {
  const { data: response } = await api.delete(
    `/api/v4/hq/franchise_fee_payouts/${payoutId}/adjustments/${adjustmentId}`
  );
  return normalizeFranchiseFeePayout(response);
};

export const initiatePayout = async (
  id: string,
  payerId: string
): Promise<FranchiseFeePayout> => {
  const { data: response } = await api.post(
    `/api/v4/hq/franchise_fee_payouts/${id}/initiate_payout`,
    {
      payer_id: payerId,
    }
  );
  return normalizeFranchiseFeePayout(response);
};
